import { motion } from "framer-motion";
import React, { useState } from "react";
import styled from "styled-components";
import { Github, LinkedIn, Phone, Email } from "../components/AllSvgs";
import { pixelCorners, pixelShadow } from "../pixel/pixelUI";

const Icons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  position: fixed;
  bottom: 1.5rem;
  left: 1.5rem;
  z-index: 10;

  @media (max-width: 600px) {
    position: relative;
    inset: auto;
    width: 100%;
    flex-direction: row;
    justify-content: center;
    padding: 1.25rem 1rem 6.5rem;
    background: ${(props) => props.theme.body};
  }
`;

const IconBadge = styled(motion.button)`
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 3px solid ${(props) => props.theme.black};
  ${pixelCorners(4)}
  ${(props) => pixelShadow(3, 3)}
  cursor: pointer;
  padding: 0;
  color: inherit;
  text-decoration: none;
`;

const Popup = styled(motion.div)`
  position: fixed;
  bottom: 8rem;
  left: 1.5rem;
  background: ${(props) => props.theme.yellow};
  color: ${(props) => props.theme.black};
  border: 3px solid ${(props) => props.theme.black};
  ${pixelCorners(6)}
  ${(props) => pixelShadow(5, 5)}
  padding: 0.4rem;
  z-index: 10;
  font-family: 'Karla', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 200px;

  @media (max-width: 600px) {
    left: 1rem;
    right: 1rem;
    bottom: 7rem;
    margin: 0 auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 4px;
  right: 8px;
  background: none;
  border: none;
  color: ${(props) => props.theme.black};
  font-size: 1rem;
  cursor: pointer;
`;

const CopyButton = styled.button`
  margin-top: 0.6rem;
  padding: 0.4rem 0.8rem;
  background: ${(props) => props.theme.blossom};
  color: #fff;
  border: 2px solid ${(props) => props.theme.black};
  cursor: pointer;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.6rem;
`;

const PopupContent = styled.div`
  margin: 0.5rem 0;
  font-weight: 700;
  word-break: break-all;
`;

const SocialIcons = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupContent, setPopupContent] = useState("");
  const [copyStatus, setCopyStatus] = useState("COPY");

  const handlePopup = (content) => {
    setPopupContent(content);
    setCopyStatus("COPY");
    setShowPopup(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(popupContent);
      setCopyStatus("COPIED!");
    } catch {
      setCopyStatus("TRY AGAIN");
    }
  };

  return (
    <>
      <Icons>
        <IconBadge
          as={motion.a}
          aria-label="Open Hermia's GitHub profile"
          target="_blank"
          rel="noreferrer"
          href="https://github.com/hermiaTT"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6, delay: 0.3 }}
        >
          <Github width={18} height={18} fill="#1A1420" />
        </IconBadge>
        <IconBadge
          as={motion.a}
          aria-label="Open Hermia's LinkedIn profile"
          target="_blank"
          rel="noreferrer"
          href="https://www.linkedin.com/in/hermiatian/"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6, delay: 0.4 }}
        >
          <LinkedIn width={18} height={18} fill="#1A1420" />
        </IconBadge>
        <IconBadge
          type="button"
          aria-label="Show phone number"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6, delay: 0.5 }}
          onClick={() => handlePopup("+1 (778)855-5154")}
        >
          <Phone width={18} height={18} fill="#1A1420" />
        </IconBadge>
        <IconBadge
          type="button"
          aria-label="Show email address"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6, delay: 0.6 }}
          onClick={() => handlePopup("hermiathm@gmail.com")}
        >
          <Email width={18} height={18} fill="#1A1420" />
        </IconBadge>
      </Icons>
      {showPopup && (
        <Popup
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
        >
          <CloseButton aria-label="Close contact popup" onClick={() => setShowPopup(false)}>×</CloseButton>
          <PopupContent>{popupContent}</PopupContent>
          <CopyButton onClick={handleCopy} aria-live="polite">{copyStatus}</CopyButton>
        </Popup>
      )}
    </>
  );
};

export default SocialIcons;
